// Auth boundary. Branches once on `cognitoEnabled` (a build-time constant, so
// the hook order is stable):
//   - enabled  → CognitoGate: gate rendering on a Cognito session, attach the
//                JWT to the shared axios client, expose the user + logout.
//   - disabled → OpenGate: local-dev passthrough with a stub user.
// Downstream code reads the user via useAuthUser() (never useAuth directly), so
// it works in both modes.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { http } from "../lib/api";
import {
  apiBearerToken,
  authUserFromProfile,
  clearExplicitSignOut,
  cognitoEnabled,
  cognitoLogoutUrl,
  cognitoMisconfigured,
  explicitlySignedOut,
  markExplicitSignOut,
  subscribeToExplicitSignOut,
} from "../lib/auth";
import type { AuthUser } from "../types/inpact";

interface AuthUserValue {
  user: AuthUser | null;
  logout: () => void;
}

const AuthUserContext = createContext<AuthUserValue>({ user: null, logout: () => {} });

export function useAuthUser(): AuthUserValue {
  return useContext(AuthUserContext);
}

const DEV_USER: AuthUser = { email: "dev@localhost", role: "admin", initials: "DV" };

export function AuthGate({ children }: { children: ReactNode }) {
  // Fail closed on a partial Cognito config — never silently passthrough.
  if (cognitoMisconfigured) {
    return (
      <AuthScreen>
        Authentication is misconfigured (incomplete Cognito settings). Please
        contact an administrator.
      </AuthScreen>
    );
  }
  return cognitoEnabled ? <CognitoGate>{children}</CognitoGate> : <OpenGate>{children}</OpenGate>;
}

function OpenGate({ children }: { children: ReactNode }) {
  return (
    <AuthUserContext.Provider value={{ user: DEV_USER, logout: () => {} }}>
      {children}
    </AuthUserContext.Provider>
  );
}

function CognitoGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const token = auth.user ? apiBearerToken(auth.user) : undefined;
  const [signedOut, setSignedOut] = useState(explicitlySignedOut);

  // Auto-redirect to the Hosted UI when unauthenticated and idle.
  useEffect(() => {
    if (!signedOut && !auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error) {
      void auth.signinRedirect();
    }
  }, [signedOut, auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, auth]);

  // Clear the bearer header if this gate ever unmounts (e.g. logout).
  useEffect(() => () => { delete http.defaults.headers.common["Authorization"]; }, []);

  useEffect(() => subscribeToExplicitSignOut(() => {
    delete http.defaults.headers.common["Authorization"];
    setSignedOut(true);
    void auth.removeUser().catch(() => {
      // The durable marker remains authoritative even if OIDC storage cleanup fails.
    });
  }), [auth]);

  const logout = async () => {
    setSignedOut(true);
    markExplicitSignOut();
    delete http.defaults.headers.common["Authorization"];
    try {
      await auth.removeUser();
    } catch {
      // Continue to Cognito logout; the durable marker still blocks local reuse.
    }
    const url = cognitoLogoutUrl();
    if (url) window.location.href = url;
  };

  if (signedOut) {
    return (
      <AuthScreen>
        Signed out.{" "}
        <button
          type="button"
          onClick={() => {
            clearExplicitSignOut();
            setSignedOut(false);
            void auth.signinRedirect();
          }}
        >
          Sign in
        </button>
      </AuthScreen>
    );
  }
  if (auth.error) {
    return (
      <AuthScreen>
        Sign-in failed: {auth.error.message}.{" "}
        <button type="button" onClick={() => void auth.signinRedirect()}>Try again</button>
      </AuthScreen>
    );
  }
  // Not ready until we hold the ID token used by the API — otherwise children's
  // React Query hooks could fire their first request before it's attached.
  if (auth.isLoading || !auth.isAuthenticated || !token) {
    return <AuthScreen>Signing in…</AuthScreen>;
  }

  // Attach SYNCHRONOUSLY, before rendering children, so the very first API call
  // carries the token (and picks up the new value on each silent renew).
  http.defaults.headers.common["Authorization"] = `Bearer ${token}`;

  const user = auth.user?.profile ? authUserFromProfile(auth.user.profile) : null;
  return (
    <AuthUserContext.Provider value={{ user, logout }}>{children}</AuthUserContext.Provider>
  );
}

function AuthScreen({ children }: { children: ReactNode }) {
  return (
    <div className="auth-screen" role="status">
      <img src="/innodia_cristal.png" alt="" aria-hidden="true" width={48} height={48} />
      <p>{children}</p>
    </div>
  );
}
